import { WaveformVisualizer } from "@/components/audio/WaveformVisualizer";
import { TextEditor, TextEditorTriggerEvent } from "@/components/TextEditor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RubricCriteriaSelector } from "@/features/rubric/RubricCriteriaSelector";
import { useApi } from "@/hooks/use-api";
import { useAppState } from "@/store/useApp";
import { SentimentType, Suggestion, SuggestionStatus } from "@/types/document";
import { Command, Loader2, Mic, Square, Wand } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { NoteEditorHeader } from "./NoteEditorHeader";
import { RubricCriteriaQuickMenu } from "./RubricCriteriaQuickMenu";
import { DoubleKey, useDoubleHotkey } from "./hooks/useDoubleHotkey";
import { useEvaluateNote } from "./hooks/useEvaluateNote";
import { useSaveHotkey } from "./hooks/useSaveHotkey";
import { useVoiceToText } from "./hooks/useVoiceToText";

interface RemarkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (updatedNote: Suggestion) => void;
  suggestion: Suggestion;
  sentiment: SentimentType | null;
}

export const NoteEditorDialog = ({
  isOpen,
  onClose,
  onSave,
  suggestion,
  sentiment,
}: RemarkDialogProps) => {
  const currentRubric = useAppState((state) => state.currentRubric);
  const currentDocument = useAppState((state) => state.currentDocument);
  const [selectedCriteriaId, setSelectedCriteriaId] = useState<string | null>(
    null
  );
  const [currentSentiment, setCurrentSentiment] =
    useState<SentimentType | null>(null);
  const api = useApi();
  const {
    updateSuggestion,
    createSuggestion,
    transcribeMedia,
    evaluateSuggestion,
  } = api;
  const [content, setContent] = useState<string>("");
  const [isCriteriaMenuOpen, setIsCriteriaMenuOpen] = useState(false);
  const [criteriaMenuPosition, setCriteriaMenuPosition] =
    useState<TextEditorTriggerEvent["caret"]>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { evaluateNote, autoEvaluateNote, isEvaluating } = useEvaluateNote({
    documentId: currentDocument?.id,
    evaluateSuggestion,
    onResult: (response) => {
      if (response.criteriaId) {
        setSelectedCriteriaId(response.criteriaId);
      }
      if (response.sentiment) {
        setCurrentSentiment(response.sentiment);
      }
    },
  });
  const handleTranscription = useCallback(
    (nextText: string) => {
      let updatedContent = "";
      setContent((prev) => {
        updatedContent = !prev.trim()
          ? nextText
          : `${prev}${prev.endsWith("\n") ? "" : "\n\n"}${nextText}`;
        return updatedContent;
      });

      if (updatedContent.trim()) {
        void autoEvaluateNote(updatedContent);
      }
    },
    [autoEvaluateNote]
  );
  const {
    isRecording,
    isTranscribing,
    transcriptionError,
    recordingError,
    isRecorderSupported,
    levels,
    startRecording,
    stopRecording,
    resetVoiceState,
  } = useVoiceToText({
    onTranscription: handleTranscription,
    transcribeMedia,
  });
  const handleStartRecording = useCallback(() => {
    void startRecording();
  }, [startRecording]);
  const handleStopRecording = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  const isSaveDisabled =
    !content ||
    !currentSentiment ||
    (selectedCriteriaId &&
      currentSentiment !== "positive" &&
      currentSentiment !== "negative") ||
    isTranscribing;
  const isEvaluateDisabled =
    !content.trim() ||
    (!!selectedCriteriaId && !!currentSentiment) ||
    isEvaluating ||
    isRecording ||
    isTranscribing ||
    isLoading;

  // replace the "Set initial state... setTimeout" effect
  useEffect(() => {
    if (!isOpen) return;
    setSelectedCriteriaId(suggestion.rubricCriteriaId ?? null);
    setCurrentSentiment(sentiment ?? null);
    setContent(suggestion.text ?? "");
  }, [
    isOpen,
    suggestion.id,
    suggestion.rubricCriteriaId,
    suggestion.text,
    sentiment,
  ]);

  useEffect(() => {
    if (!isOpen) {
      resetVoiceState();
    }
  }, [isOpen, resetVoiceState]);

  const handleCancel = () => {
    if (isRecording) {
      resetVoiceState();
    } else {
      onClose();
    }
  };

  const handleSave = async () => {
    if (!currentSentiment) return;

    try {
      setIsLoading(true);
      const criteriaId = selectedCriteriaId || undefined;
      const updatedNote = {
        ...suggestion,
        text: content,
        sentiment: currentSentiment as SentimentType,
        status: "approved" as SuggestionStatus,
        rubricCriteriaId: criteriaId,
      };

      if (updatedNote.id) {
        // Update existing suggestion
        await updateSuggestion(currentDocument!.id, updatedNote);
      } else {
        // Create new suggestion
        await createSuggestion(currentDocument!.id, updatedNote);
      }
      onSave?.(updatedNote);
      onClose();
    } catch (error) {
      console.error("Failed to save note:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useSaveHotkey({
    enabled: isOpen,
    canSave: isRecording || (!isSaveDisabled && !isLoading && !isRecording),
    onSave: isRecording ? handleStopRecording : handleSave,
  });

  useEffect(() => {
    const errorMessage = recordingError || transcriptionError;
    if (!errorMessage) return;
    toast.error(errorMessage);
  }, [recordingError, transcriptionError]);

  const toggleRecordingWithShortcut = useCallback(() => {
    if (!isRecorderSupported || isTranscribing || isLoading) {
      return;
    }

    if (isRecording) {
      handleStopRecording();
    } else {
      handleStartRecording();
    }
  }, [
    isRecorderSupported,
    isTranscribing,
    isLoading,
    isRecording,
    handleStartRecording,
    handleStopRecording,
  ]);

  const handleEvaluate = useCallback(() => {
    if (!content.trim()) return;
    void evaluateNote(content);
  }, [content, evaluateNote]);

  useDoubleHotkey({
    enabled: isOpen,
    onTrigger: toggleRecordingWithShortcut,
    key: DoubleKey.Ctrl,
  });

  useDoubleHotkey({
    enabled: isOpen && !isEvaluateDisabled,
    onTrigger: handleEvaluate,
    key: DoubleKey.Shift,
  });

  const closeCriteriaMenu = useCallback(() => {
    setIsCriteriaMenuOpen(false);
    setCriteriaMenuPosition(null);
  }, []);

  const handleEditorTrigger = useCallback(
    (event: TextEditorTriggerEvent) => {
      if (
        event.trigger !== "/" ||
        !currentRubric ||
        !event.caret ||
        isRecording ||
        isTranscribing
      ) {
        return;
      }
      setCriteriaMenuPosition(event.caret);
      setIsCriteriaMenuOpen(true);
    },
    [currentRubric, isRecording, isTranscribing]
  );

  useEffect(() => {
    if (!isOpen || isRecording) {
      closeCriteriaMenu();
    }
  }, [closeCriteriaMenu, isOpen, isRecording]);

  useEffect(() => {
    if (!currentRubric) {
      closeCriteriaMenu();
    }
  }, [closeCriteriaMenu, currentRubric]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose} modal={true}>
      <DialogContent
        className="w-[740px] top-auto! bottom-6! translate-y-0!"
        lightOverlay
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          if (isRecording) {
            e.preventDefault();
            handleCancel();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {suggestion.id ? "Edit a note" : "Capture a note"}
          </DialogTitle>
          <DialogDescription>
            Add an inline comment or grading remark
          </DialogDescription>
        </DialogHeader>

        <NoteEditorHeader
          selectedCriteriaId={selectedCriteriaId}
          sentiment={currentSentiment}
          onSentimentChange={setCurrentSentiment}
          actions={
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="bg-purple-50 border border-purple-200 rounded-sm text-purple-600 w-[20px] h-[20px] flex items-center justify-center cursor-pointer"
                  onClick={handleEvaluate}
                  disabled={isEvaluateDisabled}
                >
                  {isEvaluating ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Wand className="h-3 w-3" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">
                Evaluate sentiment and rubric (Shift + Shift)
              </TooltipContent>
            </Tooltip>
          }
        />

        {isRecording || isTranscribing ? (
          <WaveformVisualizer
            levels={levels}
            isThinking={isTranscribing && !isRecording}
            className="w-full h-[150px]"
          />
        ) : (
          <TextEditor
            className="h-[150px] text-base!"
            content={content}
            onContentChange={setContent}
            tags={selectedCriteriaId ? ["#strength", "#weakness"] : []}
            onTagInput={(tag) =>
              setCurrentSentiment(tag === "#strength" ? "positive" : "negative")
            }
            triggers={["/"]}
            scrollable
            onTrigger={handleEditorTrigger}
            actions={
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={
                      isRecording ? handleStopRecording : handleStartRecording
                    }
                    disabled={
                      !isRecorderSupported || isTranscribing || isLoading
                    }
                    className="text-muted-for hover:text-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    {isRecording ? (
                      <Square className="h-4 w-4" />
                    ) : (
                      <Mic className="h-4 w-4" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {isRecording ? "Stop recording" : "Start recording"} (Ctrl +
                  Ctrl)
                </TooltipContent>
              </Tooltip>
            }
          />
        )}

        <DialogFooter className="flex justify-between items-center">
          {currentRubric && (
            <RubricCriteriaSelector
              rubric={currentRubric}
              selectedCriteriaId={selectedCriteriaId}
              onCriteriaSelect={setSelectedCriteriaId}
              onClear={() => setSelectedCriteriaId(null)}
            />
          )}
          <div className="flex gap-2 ml-auto">
            <div className="w-[260px] flex gap-2 justify-end" >
              {isRecording && (
                <div className="flex items-center">
                  <Badge
                    variant="default"
                    className="mr-2"
                  >
                    ⌘⏎
                  </Badge>
                  <span className="text-muted-foreground text-xs">to transcribe</span>

                  <Badge
                    variant="secondary"
                    className="text-secondary-foreground ml-4 mr-2"
                  >
                    ESC
                  </Badge>
                  <span className="text-muted-foreground text-xs">to cancel</span>
                </div>
              )}

              {!isRecording && (
                <>
                  <Button
                    disabled={isRecording}
                    variant="outline"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={isSaveDisabled || isLoading || isRecording}
                  >
                    {isLoading ? "Saving..." : "Save note"}
                    <span className="text-xs flex items-center gap-1 ml-2">
                      <Command className="h-3 w-3" />
                      <span>↵</span>
                    </span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
      <RubricCriteriaQuickMenu
        open={isCriteriaMenuOpen && !!currentRubric}
        position={criteriaMenuPosition}
        criteria={currentRubric?.criteria ?? []}
        selectedCriteriaId={selectedCriteriaId}
        onSelect={(criteriaId) => {
          setSelectedCriteriaId(criteriaId);
        }}
        onClose={closeCriteriaMenu}
      />
    </Dialog>
  );
};
